import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, api, setSession, roleHome } from "@/lib/api";
import { offlineLogin, saveOfflineLogin, getOfflineProfiles, syncOfflineLogins } from "@/lib/offlineAuth";
import PostLoginSetup from "@/components/auth/PostLoginSetup";
import BrandLogo from "@/components/BrandLogo";
import LanguageSelect from "@/components/LanguageSelect";
import { useT } from "@/lib/i18n";
import { TEST } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ShieldCheck, Smartphone, KeyRound, RotateCcw, Eye, EyeOff, LockKeyhole, ChevronRight, UserRound } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { LoginHero } from "@/components/auth/LoginHero";
import { GoogleSignInButton } from "@/components/auth/GoogleAuth";
import {
  deviceHeaders, mapAuthError, ModeChip, OfflineContinueCard, WhatsAppSupport,
  MobileNumberField, GeoBlockedScreen, WHATSAPP_SUPPORT_NUMBER,
} from "@/components/auth/loginParts";

export default function LoginPage() {
  const nav = useNavigate();
  const { t } = useT();
  const [tab, setTab] = useState("password"); // "password" | "pin" | "otp"
  const [step, setStep] = useState("mobile"); // "mobile" | "otp" | "not_registered"
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  // Global login-method toggles (Super Admin controlled)
  const [authSettings, setAuthSettings] = useState({ password_enabled: true, pin_enabled: true, otp_enabled: true });
  useEffect(() => {
    axios.get(`${API_BASE}/auth/settings`).then((r) => setAuthSettings(r.data)).catch(() => {});
  }, []);

  // If the active tab's method gets disabled by admin, fall back to an enabled one.
  useEffect(() => {
    if (tab === "password" && !authSettings.password_enabled) setTab(authSettings.pin_enabled ? "pin" : "otp");
    if (tab === "pin" && !authSettings.pin_enabled) setTab(authSettings.password_enabled ? "password" : "otp");
  }, [authSettings, tab]);

  // Geo Access Control — restrict login to India (marketing pages stay public)
  const [geo, setGeo] = useState(null);
  const [adminOverride, setAdminOverride] = useState(false);
  useEffect(() => {
    api.get("/geo/check").then((r) => setGeo(r.data)).catch(() => {});
  }, []);

  // Password login state
  const [pwMobile, setPwMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  // Quick PIN login state
  const [pinMobile, setPinMobile] = useState("");
  const [pin, setPin] = useState("");
  const [pinProfile, setPinProfile] = useState(null); // stored offline profile for prefill
  useEffect(() => {
    getOfflineProfiles().then((profiles) => {
      const withPin = profiles.find((p) => p.hasPin) || profiles[0];
      if (withPin) {
        setPinProfile(withPin);
        setPinMobile((m) => m || withPin.mobile);
      }
    }).catch(() => {});
  }, []);

  // Post-login setup flow (forced password change / first password / create PIN)
  const [setup, setSetup] = useState(null); // { token, user, password?, method }

  // Forgot-password flow state ("idle" | "otp-sent")
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState("mobile"); // "mobile" | "otp"
  const [forgotMobile, setForgotMobile] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);

  // Privacy Policy agreement (persisted so returning users stay opted in)
  const [privacyOk, setPrivacyOk] = useState(() => localStorage.getItem("am_privacy_ok") === "1");
  const togglePrivacy = (v) => {
    setPrivacyOk(v);
    localStorage.setItem("am_privacy_ok", v ? "1" : "0");
  };

  /** Offline login success — go straight in (setup dialogs need internet). */
  const finishLogin = (data) => {
    setSession(data.token, data.user);
    toast.success(t("msg.welcome", { name: data.user.name }));
    nav(roleHome(data.user.role), { replace: true });
  };

  /** Online login success — run the post-login setup sequence first. */
  const startSetup = (data, method, plainPassword) => {
    setSession(data.token, data.user);
    syncOfflineLogins();
    setSetup({ token: data.token, user: data.user, password: plainPassword, method });
  };

  const onSetupDone = (updatedUser) => {
    setSession(setup.token, updatedUser);
    setSetup(null);
    toast.success(t("msg.welcome", { name: updatedUser.name }));
    nav(roleHome(updatedUser.role), { replace: true });
  };

  const sendOtp = async () => {
    if (!/^\d{6,15}$/.test(mobile)) { toast.error(t("msg.invalidMobile")); return; }
    if (!privacyOk) { toast.error(t("auth.privacyRequired")); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/send-otp`, { mobile }, { headers: deviceHeaders() });
      if (r?.data?.ok !== false) toast.success(t("msg.otpSent"));
      setStep("otp");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.otpFailed"));
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error(t("msg.otpInvalid")); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/verify-otp`, { mobile, otp }, { headers: deviceHeaders() });
      startSetup(r.data, "otp", null);
    } catch (e) {
      if (e?.response?.status === 404) {
        setStep("not_registered");
      } else {
        toast.error(mapAuthError(t, e?.response?.data?.detail, "otp"));
      }
    } finally { setLoading(false); }
  };

  const offlinePasswordLogin = async () => {
    try {
      const data = await offlineLogin(pwMobile, password, "password");
      finishLogin(data);
    } catch (err) {
      toast.error(mapAuthError(t, err.message, "password"));
    }
  };

  const verifyPassword = async () => {
    if (!/^\d{6,15}$/.test(pwMobile)) { toast.error(t("msg.invalidMobile")); return; }
    if (!password) { toast.error(t("msg.enterPassword")); return; }
    setLoading(true);
    if (!navigator.onLine) {
      await offlinePasswordLogin();
      setLoading(false);
      return;
    }
    try {
      const r = await axios.post(`${API_BASE}/auth/verify-password`, { mobile: pwMobile, password }, { headers: deviceHeaders() });
      // Store encrypted offline credentials so this device can sign in without internet.
      await saveOfflineLogin(pwMobile, password, r.data.token, r.data.user).catch(() => {});
      startSetup(r.data, "password", password);
    } catch (e) {
      if (!e?.response) {
        await offlinePasswordLogin(); // server unreachable — offline fallback
      } else if (e.response?.data?.detail === "new_device_otp_required") {
        setMobile(pwMobile);
        setStep("mobile");
        setTab("otp");
        toast.info(t("auth.newDeviceOtp"));
      } else {
        toast.error(mapAuthError(t, e?.response?.data?.detail, "password"));
      }
    } finally { setLoading(false); }
  };

  const verifyPin = async () => {
    if (!/^\d{6,15}$/.test(pinMobile)) { toast.error(t("msg.invalidMobile")); return; }
    if (!/^\d{4}$|^\d{6}$/.test(pin)) { toast.error(t("auth.pinFormat")); return; }
    setLoading(true);
    if (!navigator.onLine) {
      try {
        const data = await offlineLogin(pinMobile, pin, "pin");
        finishLogin(data);
      } catch (err) {
        toast.error(mapAuthError(t, err.message, "pin"));
      }
      setLoading(false);
      return;
    }
    try {
      const r = await axios.post(`${API_BASE}/auth/verify-pin`, { mobile: pinMobile, pin }, { headers: deviceHeaders() });
      startSetup(r.data, "pin", null);
    } catch (e) {
      if (!e?.response) {
        try {
          const data = await offlineLogin(pinMobile, pin, "pin");
          finishLogin(data);
        } catch (err) {
          toast.error(mapAuthError(t, err.message, "pin"));
        }
      } else {
        toast.error(mapAuthError(t, e?.response?.data?.detail, "pin"));
      }
    } finally { setLoading(false); }
  };

  const startForgot = () => {
    setForgotMobile(pwMobile || "");
    setForgotOtp("");
    setNewPassword("");
    setForgotStep("mobile");
    setForgotOpen(true);
  };

  const forgotSendOtp = async () => {
    if (!/^\d{6,15}$/.test(forgotMobile)) { toast.error(t("msg.invalidMobile")); return; }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/send-otp`, { mobile: forgotMobile });
      toast.success(t("msg.otpSent"));
      setForgotStep("otp");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.otpFailed"));
    } finally { setLoading(false); }
  };

  const forgotSubmit = async () => {
    if (forgotOtp.length !== 6) { toast.error(t("msg.otpInvalid")); return; }
    if (newPassword.length < 6) { toast.error(t("msg.passwordShort")); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/auth/reset-password`, {
        mobile: forgotMobile, otp: forgotOtp, new_password: newPassword,
      }, { headers: deviceHeaders() });
      toast.success(t("msg.passwordUpdated"));
      setForgotOpen(false);
      await saveOfflineLogin(forgotMobile, newPassword, r.data.token, r.data.user).catch(() => {});
      startSetup(r.data, "password", newPassword);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.resetFailed"));
    } finally { setLoading(false); }
  };

  const tryAnotherNumber = () => {
    setStep("mobile");
    setMobile("");
    setOtp("");
    setPwMobile("");
    setPassword("");
    setForgotOpen(false);
  };

  // Outside India + India-only mode → restriction screen (Admin sign in stays reachable).
  if (geo && geo.india_only && !geo.allowed && !adminOverride) {
    return <GeoBlockedScreen onAdminLogin={() => setAdminOverride(true)} />;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left art */}
      <LoginHero />

      {/* Right form — centered */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="lg:hidden flex items-center gap-3" data-testid="auth-brand-mobile">
              <BrandLogo size="login" mobileSize="login" alt="AnimalMela" bg />
              <span className="font-display font-extrabold text-2xl">AnimalMela</span>
            </Link>
            <div className="lg:hidden" />
            <LanguageSelect compact />
          </div>

          {step !== "not_registered" && (
            <>
              <h1 className="font-display text-3xl font-extrabold text-center">{t("auth.title")}</h1>
              <p className="text-muted-foreground mt-2 mb-3 text-center">{t("auth.subtitle")}</p>
              <div className="text-center"><ModeChip /></div>

              <OfflineContinueCard />

              <Tabs value={tab} onValueChange={(v) => { setTab(v); setForgotOpen(false); }} className="w-full">
                <TabsList
                  className={`grid w-full mb-6 h-auto p-1 ${[authSettings.password_enabled, authSettings.pin_enabled, authSettings.otp_enabled].filter(Boolean).length === 3 ? "grid-cols-3" : "grid-cols-2"}`}
                  data-testid="auth-tabs"
                >
                  {authSettings.password_enabled && (
                    <TabsTrigger value="password" className="py-2.5" data-testid="auth-tab-password">{t("auth.tabPassword")}</TabsTrigger>
                  )}
                  {authSettings.pin_enabled && (
                    <TabsTrigger value="pin" className="py-2.5" data-testid="auth-tab-pin">{t("auth.tabPin")}</TabsTrigger>
                  )}
                  <TabsTrigger value="otp" className="py-2.5" data-testid="auth-tab-otp">{t("auth.tabOtp")}</TabsTrigger>
                </TabsList>

                <TabsContent value="otp" className="mt-0">
                  {step === "mobile" && (
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground rounded-xl bg-muted px-3 py-2" data-testid="auth-otp-note">
                        {t("auth.otpOnlyNote")}
                      </p>
                      <div>
                        <label className="text-sm font-semibold mb-2 block">{t("auth.mobile")}</label>
                        <MobileNumberField
                          value={mobile}
                          onChange={setMobile}
                          testId={TEST.authMobile}
                          placeholder={t("auth.mobilePlaceholder")}
                        />
                      </div>
                      <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid="auth-privacy-checkbox"
                          checked={privacyOk}
                          onChange={(e) => togglePrivacy(e.target.checked)}
                          className="mt-0.5 h-4 w-4 accent-primary cursor-pointer"
                        />
                        <span>
                          {t("auth.privacyAgree")}{" "}
                          <a href="/privacy-policy" target="_blank" rel="noreferrer"
                            className="font-semibold text-primary underline underline-offset-2"
                            data-testid="auth-privacy-link" onClick={(e) => e.stopPropagation()}>
                            Privacy Policy
                          </a>{" "}{t("auth.and")}{" "}
                          <a href="/terms-and-conditions" target="_blank" rel="noreferrer"
                            className="font-semibold text-primary underline underline-offset-2"
                            data-testid="auth-terms-link" onClick={(e) => e.stopPropagation()}>
                            Terms &amp; Conditions
                          </a>.
                        </span>
                      </label>
                      <Button
                        data-testid={TEST.authSendOtp}
                        onClick={sendOtp}
                        disabled={loading}
                        className="am-btn-primary w-full py-6 text-base group"
                      >
                        <span className="flex-1 text-center">{loading ? t("auth.sending") : t("auth.sendOtp")}</span>
                        <ChevronRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5" />
                      </Button>

                      <WhatsAppSupport />
                    </div>
                  )}

                  {step === "otp" && (
                    <div className="space-y-4">
                      <button
                        data-testid={TEST.authBack}
                        onClick={() => { setStep("mobile"); setOtp(""); }}
                        className="text-sm font-semibold inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
                      >
                        <ArrowLeft className="w-4 h-4" /> {t("auth.changeNumber")}
                      </button>
                      <div className="am-card p-4 bg-accent border-accent">
                        <div className="text-xs uppercase tracking-widest font-bold text-accent-foreground">{t("auth.otpSentTo")}</div>
                        <div className="font-mono font-semibold text-accent-foreground">+91 {mobile}</div>
                      </div>
                      <div>
                        <label className="text-sm font-semibold mb-2 block">{t("auth.otp")}</label>
                        <Input
                          data-testid={TEST.authOtp}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="••••••"
                          className="am-input font-mono text-center text-2xl tracking-[0.5em]"
                          inputMode="numeric"
                          maxLength={6}
                        />
                      </div>
                      <Button
                        data-testid={TEST.authVerify}
                        onClick={verifyOtp}
                        disabled={loading}
                        className="am-btn-primary w-full py-6 text-base"
                      >
                        {loading ? t("auth.verifying") : t("auth.verify")}
                      </Button>

                      <WhatsAppSupport />
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="pin" className="mt-0">
                  <div className="space-y-4" data-testid="auth-pin-panel">
                    {pinProfile?.hasPin && pinMobile === pinProfile.mobile && (
                      <div className="am-card p-3 bg-accent border-accent flex items-center gap-3" data-testid="auth-pin-profile">
                        <span className="w-9 h-9 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
                          <UserRound className="w-4 h-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold truncate">{pinProfile.name || `+91 ${pinProfile.mobile}`}</div>
                          <div className="text-xs text-muted-foreground font-mono">+91 {pinProfile.mobile}</div>
                        </div>
                        <button
                          data-testid="auth-pin-change-user"
                          onClick={() => setPinMobile("")}
                          className="text-xs font-semibold text-primary hover:underline shrink-0"
                        >
                          {t("auth.changeNumber")}
                        </button>
                      </div>
                    )}
                    {!(pinProfile?.hasPin && pinMobile === pinProfile.mobile) && (
                      <div>
                        <label className="text-sm font-semibold mb-2 block">{t("auth.mobile")}</label>
                        <MobileNumberField
                          value={pinMobile}
                          onChange={setPinMobile}
                          testId="auth-pin-mobile-input"
                          placeholder={t("auth.mobilePlaceholder")}
                        />
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-semibold mb-2 block">{t("auth.pin")}</label>
                      <Input
                        data-testid="auth-pin-input"
                        type="password"
                        inputMode="numeric"
                        maxLength={6}
                        value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="••••"
                        className="am-input font-mono text-center text-2xl tracking-[0.5em]"
                        onKeyDown={(e) => { if (e.key === "Enter") verifyPin(); }}
                      />
                    </div>
                    <Button
                      data-testid="auth-pin-signin-btn"
                      onClick={verifyPin}
                      disabled={loading}
                      className="am-btn-primary w-full py-6 text-base"
                    >
                      <KeyRound className="w-4 h-4 mr-2" /> {loading ? t("auth.signingIn") : t("auth.signInWithPin")}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center" data-testid="auth-pin-hint">
                      {t("auth.pinHint")}
                    </p>

                    <WhatsAppSupport />
                  </div>
                </TabsContent>

                <TabsContent value="password" className="mt-0">
                  {!forgotOpen && (
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold mb-2 block">{t("auth.mobile")}</label>
                        <MobileNumberField
                          value={pwMobile}
                          onChange={setPwMobile}
                          testId="auth-pw-mobile-input"
                          placeholder={t("auth.mobilePlaceholder")}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold mb-2 block">{t("auth.password")}</label>
                        <div className="relative">
                          <Input
                            data-testid="auth-pw-input"
                            type={showPw ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t("auth.passwordPlaceholder")}
                            className="am-input pr-11"
                            onKeyDown={(e) => { if (e.key === "Enter") verifyPassword(); }}
                          />
                          <button
                            type="button"
                            data-testid="auth-pw-toggle-btn"
                            onClick={() => setShowPw((s) => !s)}
                            aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
                            className="absolute inset-y-0 right-2 my-auto h-9 w-9 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                          >
                            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <Button
                        data-testid="auth-pw-signin-btn"
                        onClick={verifyPassword}
                        disabled={loading}
                        className="am-btn-primary w-full py-6 text-base"
                      >
                        <LockKeyhole className="w-4 h-4 mr-2" /> {loading ? t("auth.signingIn") : t("auth.signIn")}
                      </Button>
                      <div className="text-center">
                        <button
                          type="button"
                          data-testid="auth-forgot-btn"
                          onClick={startForgot}
                          className="text-sm font-semibold text-primary hover:underline"
                        >
                          {t("auth.forgot")}
                        </button>
                      </div>

                      <WhatsAppSupport />
                    </div>
                  )}

                  {forgotOpen && (
                    <div className="space-y-4" data-testid="auth-forgot-panel">
                      <button
                        onClick={() => setForgotOpen(false)}
                        className="text-sm font-semibold inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
                        data-testid="auth-forgot-back-btn"
                      >
                        <ArrowLeft className="w-4 h-4" /> {t("auth.backToSignin")}
                      </button>
                      <h2 className="font-display text-xl font-extrabold">{t("auth.forgotTitle")}</h2>

                      {forgotStep === "mobile" && (
                        <>
                          <div>
                            <label className="text-sm font-semibold mb-2 block">{t("auth.registeredMobile")}</label>
                            <MobileNumberField
                              value={forgotMobile}
                              onChange={setForgotMobile}
                              testId="auth-forgot-mobile-input"
                              placeholder={t("auth.mobilePlaceholder")}
                            />
                          </div>
                          <Button
                            data-testid="auth-forgot-send-otp-btn"
                            onClick={forgotSendOtp}
                            disabled={loading}
                            className="am-btn-primary w-full py-6 text-base group"
                          >
                            <span className="flex-1 text-center">{loading ? t("auth.sending") : t("auth.sendOtp")}</span>
                            <ChevronRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-0.5" />
                          </Button>
                        </>
                      )}

                      {forgotStep === "otp" && (
                        <>
                          <div className="am-card p-4 bg-accent border-accent">
                            <div className="text-xs uppercase tracking-widest font-bold text-accent-foreground">{t("auth.otpSentTo")}</div>
                            <div className="font-mono font-semibold text-accent-foreground">+91 {forgotMobile}</div>
                          </div>
                          <div>
                            <label className="text-sm font-semibold mb-2 block">{t("auth.otp")}</label>
                            <Input
                              data-testid="auth-forgot-otp-input"
                              value={forgotOtp}
                              onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                              placeholder="••••••"
                              className="am-input font-mono text-center text-2xl tracking-[0.5em]"
                              inputMode="numeric"
                              maxLength={6}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-semibold mb-2 block">{t("auth.newPassword")}</label>
                            <div className="relative">
                              <Input
                                data-testid="auth-forgot-newpw-input"
                                type={showNewPw ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder={t("auth.newPasswordHint")}
                                className="am-input pr-11"
                              />
                              <button
                                type="button"
                                onClick={() => setShowNewPw((s) => !s)}
                                aria-label={showNewPw ? t("auth.hidePassword") : t("auth.showPassword")}
                                className="absolute inset-y-0 right-2 my-auto h-9 w-9 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                              >
                                {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <Button
                            data-testid="auth-forgot-submit-btn"
                            onClick={forgotSubmit}
                            disabled={loading}
                            className="am-btn-primary w-full py-6 text-base"
                          >
                            {loading ? t("auth.updating") : t("auth.setNewPassword")}
                          </Button>
                        </>
                      )}

                      <WhatsAppSupport />
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <GoogleSignInButton />
            </>
          )}

          {step === "not_registered" && (
            <div className="space-y-5" data-testid="auth-not-registered">
              <div className="w-16 h-16 mx-auto rounded-3xl bg-accent grid place-items-center">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
              <div className="text-center">
                <h1 className="font-display text-2xl font-extrabold">{t("auth.notRegisteredTitle")}</h1>
                <p className="text-muted-foreground mt-3 leading-relaxed">
                  {t("auth.notRegisteredMsg")}
                </p>
                <div className="mt-4 am-chip mx-auto">
                  <Smartphone className="w-3.5 h-3.5" /> +91 {mobile}
                </div>
              </div>

              <div className="grid gap-2 pt-2">
                <a
                  href={`https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent("Hello AnimalMela support, I need access to my Pashu Haat account.")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="auth-contact-support"
                  className="am-btn-primary w-full py-4 text-base text-center inline-flex items-center justify-center"
                >
                  <WhatsAppIcon className="w-5 h-5 mr-2" color="#ffffff" /> {t("auth.contactSupport")}
                </a>
                <Button
                  variant="outline"
                  className="rounded-full w-full py-6 text-base"
                  onClick={tryAnotherNumber}
                  data-testid="auth-try-another"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> {t("auth.tryAnother")}
                </Button>
              </div>

              <WhatsAppSupport />
            </div>
          )}

          <div className="mt-6 text-center flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <a href="/privacy-policy" target="_blank" rel="noreferrer"
              className="underline underline-offset-4 hover:text-foreground"
              data-testid="login-privacy-link">
              Privacy Policy
            </a>
            <span aria-hidden="true">|</span>
            <a href="/terms-and-conditions" target="_blank" rel="noreferrer"
              className="underline underline-offset-4 hover:text-foreground"
              data-testid="login-terms-link">
              Terms &amp; Conditions
            </a>
            <span aria-hidden="true">|</span>
            <a href="/refund-cancellation-policy" target="_blank" rel="noreferrer"
              className="underline underline-offset-4 hover:text-foreground"
              data-testid="login-refund-link">
              Refund Policy
            </a>
          </div>
        </div>
      </div>

      {setup && <PostLoginSetup pending={setup} onDone={onSetupDone} />}
    </div>
  );
}
